import React from 'react';
import Masonry, {
  getMasonryDriver,
  MasonryConfig,
} from '@leagueplatform/masonry';
import { MasonryEngine } from '@leagueplatform/masonry-engine';
import { useDocumentTitle } from '@leagueplatform/web-common';
import { usePageViewAnalytics } from '@leagueplatform/analytics';
import { useIntl } from '@leagueplatform/locales';
import { styled, Box } from '@leagueplatform/genesis-core';
import { useFeatureFlagQuery } from '@leagueplatform/user-profile-api';
import { FEATURE_FLAGS, HOME_ROOT_NODE_ID } from 'common/constants';
import { useTTVTracking } from 'hooks/use-ttv-tracking.hook';
import { MasonryPageContainer } from '../masonry-container.page';

type HomePageProps = {
  isMasonryV2Enabled: boolean;
  masonryV1Config?: MasonryConfig;
};

const HomePageBannerStyles = styled(Box, {
  // max banner height 160px (40px + 96px + 24px)
  '.masonry-page-header-background': {
    marginTop: '40px',
    marginBottom: '40px', // Adds extra 16px margin to the 24px below the header CTA button
    height: '96px',

    '@mobile': {
      height: 'auto',
    },

    '.GDS-button': {
      // Banner CTA button
      marginTop: '6px',
    },

    '&:before': {
      // Banner background color
      // "top" controls the height of the background. Values below are 160px from the default values.
      // Default values for modules/masonry-widgets/src/components/common/page-header-background/page-header-background.tsx.
      '@desktop': {
        top: '-19864px',
      },
      '@laptop': {
        top: '-19864px',
      },
      '@tablet': {
        top: '-19864px',
      },
      '@mobileLandscape': {
        top: '-4840px',
      },
    },
  },
});

const MasonryV1Container = ({ config }: { config: MasonryConfig }) => {
  const HomePageMasonry = React.useMemo(() => {
    if (!config) return null;
    return Masonry.init(config);
  }, [config]);

  return HomePageMasonry && <HomePageMasonry.Provider />;
};

const MasonryV2Container = () => {
  const MasonryHomeDriver = getMasonryDriver({
    rootNodeId: HOME_ROOT_NODE_ID,
  });

  return <MasonryEngine driver={MasonryHomeDriver} />;
};

export const HomePage = ({
  isMasonryV2Enabled,
  masonryV1Config,
}: HomePageProps) => {
  usePageViewAnalytics({ screen_name: 'Home' });
  useTTVTracking();
  const { formatMessage } = useIntl();
  useDocumentTitle(formatMessage({ id: 'HOME_PAGE' }));
  const { data: isHomeScreenEnhancementsEnabled } = useFeatureFlagQuery(
    FEATURE_FLAGS.ENABLE_HOME_SCREEN_ENHANCEMENTS,
  );

  const HomePageWrapper = isHomeScreenEnhancementsEnabled
    ? HomePageBannerStyles
    : React.Fragment;

  return (
    <HomePageWrapper>
      <MasonryPageContainer>
        {isMasonryV2Enabled ? (
          <MasonryV2Container />
        ) : (
          <MasonryV1Container config={masonryV1Config!} />
        )}
      </MasonryPageContainer>
    </HomePageWrapper>
  );
};
